/**
 * HISIP-005: Creative Profiles List Page
 * 
 * Displays profiles table with actions (Edit, Delete, Set Default)
 * Uses futuristic design system consistent with dashboard styling
 */

import React, { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Star, StarOff } from 'lucide-react';
import { useProfiles, prefetchProfileDetails } from '@/hooks/useProfiles';
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import CreateProfileModal from '@/components/profiles/CreateProfileModal';
// EditProfileModal removed; inline editor is the only editing flow
import ConfirmDeleteModal from '@/components/profiles/ConfirmDeleteModal';
import InlineProfileEditor from '@/components/profiles/InlineProfileEditor';
import MosaicOverlay from '@/components/profiles/MosaicOverlay';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { formatTimeAgo } from '@/utils/format';

const ProfilesListPage: React.FC = () => {
  // const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  // edit modal state removed
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [collapsingId, setCollapsingId] = useState<string | null>(null);

  const {
    profiles,
    loading,
    error,
    refetch,
    createProfile,
    deleteProfile,
    setDefaultProfile
  } = useProfiles('owner');

  const handleCreateProfile = async (data: { name: string; description?: string }) => {
    const profile = await createProfile(data);
    if (profile) {
      setShowCreateModal(false);
      setExpandedId(profile.id);
      refetch();
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    setDeleteConfirmId(profileId);
  };

  const handleSetDefault = async (profileId: string) => {
    await setDefaultProfile(profileId);
  };

  // Row expansion is handled inline via the <summary> onClick to keep details/summary semantics

  return (
    <div className="void-foundation min-h-screen">
      <AtmosphericBackground />
      
      <div className="relative z-10 p-6 space-y-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-light text-gradient-cyan tracking-wider mb-2">
              CREATIVE PROFILES
            </h1>
            <p className="text-white/50 font-mono text-sm">
              CONFIGURE AI PERSONAS, STYLES & PIPELINE SETTINGS
            </p>
          </div>
          
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">CREATE PROFILE</span>
          </button>
        </div>

        {/* Profiles List (details/summary accordion) */}
        <HolographicCard glow="cyan" intensity="subtle">
          <CardHeader 
            title="PROFILE CONFIGURATIONS" 
            subtitle="Manage creative personas and pipeline settings"
            subtitleClassName="text-white/50"
            icon={<Star className="w-5 h-5" />}
          />
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="loading-spinner w-8 h-8 border-cyan-400"></div>
                <span className="ml-3 text-cyan-400 font-mono">LOADING PROFILES...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-400 font-mono mb-4">ERROR: {error}</div>
                <button 
                  onClick={refetch}
                  className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
                >
                  RETRY
                </button>
              </div>
            ) : profiles.length === 0 ? (
              // Empty state
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Star className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-mono text-cyan-400 mb-2">NO PROFILES CONFIGURED</h3>
                <p className="text-white/50 mb-6 font-mono text-sm">
                  Create your first creative profile to define AI personas and pipeline settings
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
                >
                  CREATE FIRST PROFILE
                </button>
              </div>
            ) : (
              // Profiles accordion list using details/summary for robust toggle
              <div>
                {/* Header row */}
                <div className="hidden md:grid grid-cols-12 border-b border-cyan-500/20">
                  <div className="col-span-4 py-3 px-4 font-mono text-cyan-400 text-xs uppercase tracking-wider">Name</div>
                  <div className="col-span-2 py-3 px-4 font-mono text-cyan-400 text-xs uppercase tracking-wider">Preset</div>
                  <div className="col-span-2 py-3 px-4 font-mono text-cyan-400 text-xs uppercase tracking-wider">Status</div>
                  <div className="col-span-2 py-3 px-4 font-mono text-cyan-400 text-xs uppercase tracking-wider">Updated</div>
                  <div className="col-span-2 py-3 px-4 font-mono text-cyan-400 text-xs uppercase tracking-wider text-right">Actions</div>
                </div>

                {profiles
                  .filter((p) => !(p as any).is_blank)
                  .map((profile) => (
                    <details key={profile.id} open={expandedId === profile.id} className={cn("group border-b", expandedId === profile.id ? "border-cyan-500/30" : "border-white/5") }>
                      <summary
                        className={cn(
                          "grid grid-cols-12 items-center py-4 px-4 transition-colors cursor-pointer list-none",
                          expandedId === profile.id ? "bg-cyan-500/5" : "hover:bg-cyan-500/5"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          if (expandedId === profile.id) {
                            // Play quick themed minimize (overlay) before collapsing
                            setCollapsingId(profile.id);
                            setTimeout(() => {
                              setExpandedId(null);
                              setCollapsingId(null);
                            }, 320);
                          } else {
                            prefetchProfileDetails(profile.id);
                            setExpandedId(profile.id);
                          }
                        }}
                      >
                        {/* Name */}
                        <div className="col-span-12 md:col-span-4">
                          <div className="font-mono text-white/90 group-hover:text-cyan-400 transition-colors">
                            {profile.name}
                          </div>
                          {profile.description && (
                            <div className="text-xs text-white/50 mt-1 line-clamp-1">
                              {profile.description}
                            </div>
                          )}
                        </div>

                        {/* Preset */}
                        <div className="hidden md:block md:col-span-2">
                          {profile.preset_key ? (
                            <span className={cn(
                              "inline-flex items-center px-2 py-1 rounded-full text-xs font-mono border",
                              profile.preset_key === 'cinematic' && "bg-purple-500/20 text-purple-400 border-purple-500/30",
                              profile.preset_key === 'advertiser' && "bg-blue-500/20 text-blue-400 border-blue-500/30", 
                              profile.preset_key === 'educational' && "bg-green-500/20 text-green-400 border-green-500/30",
                              profile.preset_key === 'animated' && "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                            )}>
                              {profile.preset_key.toUpperCase()}
                            </span>
                          ) : (
                            <span className="text-white/30 font-mono text-xs">CUSTOM</span>
                          )}
                        </div>

                        {/* Status */}
                        <div className="hidden md:block md:col-span-2">
                          {profile.is_default ? (
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4 text-yellow-400 fill-current" />
                              <span className="text-yellow-400 font-mono text-xs">DEFAULT</span>
                            </div>
                          ) : (
                            <span className="text-white/40 font-mono text-xs">ACTIVE</span>
                          )}
                        </div>

                        {/* Updated */}
                        <div className="hidden md:block md:col-span-2">
                          <span className="text-white/50 font-mono text-xs">
                            {formatTimeAgo(new Date(profile.updated_at))}
                          </span>
                        </div>

                        {/* Actions - prevent toggle with preventDefault */}
                        <div className="col-span-12 md:col-span-2">
                          <div className="flex items-center gap-2 justify-end">
                            {!profile.is_default && (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); handleSetDefault(profile.id); }}
                                className="p-2 text-white/50 hover:text-cyan-400 border border-transparent hover:border-cyan-400/30 rounded-md transition-colors"
                                title="Set as default"
                              >
                                <StarOff className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); handleDeleteProfile(profile.id); }}
                              className={cn(
                                "p-2 transition-colors border border-transparent rounded-md",
                                deleteConfirmId === profile.id
                                  ? "text-red-400 hover:text-red-300"
                                  : "text-white/50 hover:text-red-400 hover:border-red-400/30"
                              )}
                              title="Delete profile"
                              disabled={profile.is_default}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </summary>

                      <AnimatePresence initial={false}>
                        {(expandedId === profile.id || collapsingId === profile.id) && (
                          <motion.div
                            key={`${profile.id}-reveal`}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.6, ease: 'easeInOut' }}
                            className="px-0 relative"
                          >
                            <div className="overflow-hidden relative">
                              <MosaicOverlay rows={18} cols={30} reveal={collapsingId !== profile.id} durationMs={collapsingId === profile.id ? 320 : 1200} />
                              <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4, duration: 0.6, ease: 'easeOut' }}
                              >
                                <InlineProfileEditor 
                                  profileId={profile.id} 
                                  onSaved={() => {
                                    refetch();
                                    setExpandedId(null);
                                  }} 
                                  onCancel={() => setExpandedId(null)} 
                                />
                              </motion.div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </details>
                ))}
              </div>
            )}
          </CardContent>
        </HolographicCard>

        {/* Back Navigation */}
        <div className="flex justify-start">
          {/* Removed back-to-settings: Profiles are independent */}
        </div>
      </div>

      {/* Create Profile Modal */}
      {showCreateModal && (
        <CreateProfileModal
          onSubmit={handleCreateProfile}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit Modal removed */}

      {/* Confirm Delete Modal */}
      {deleteConfirmId && (
        <ConfirmDeleteModal
          profileName={profiles.find(p => p.id === deleteConfirmId)?.name || ''}
          loading={deleting}
          onCancel={() => setDeleteConfirmId(null)}
          onConfirm={async () => {
            try {
              setDeleting(true);
              await deleteProfile(deleteConfirmId);
            } finally {
              setDeleting(false);
              setDeleteConfirmId(null);
              await refetch();
            }
          }}
        />
      )}
    </div>
  );
};

export default ProfilesListPage;